import React, { useEffect, useState } from 'react';
import { TbBrandWhatsapp, TbDeviceMobileMessage, TbMessage } from 'react-icons/tb';
import { isPhoneValid, sanitizeText } from '../../../Utils/Helpers';
import ButtonPrimary from '../../Form/ButtonPrimary';
import { ImSpinner8 } from 'react-icons/im';
import { useCartHook } from '../../../Hooks/CartHook';
import { formatPhone } from '../../../Utils/Formatters';
import { IMaskInput } from 'react-imask';
import CustomerService from '../../../Services/CustomerService';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import CountdownTimer from '../CountdownTimer';
import { useLoginContext } from '../../../Contexts/LoginContext';
import { useTranslation } from 'react-i18next';

interface FormConfirmMobilePhoneProps {
    phone: string;
    onSuccess: CallableFunction;
}

const FormConfirmMobilePhone = ({ phone, onSuccess }: FormConfirmMobilePhoneProps) => {

    const { t } = useTranslation();
    const { orderType } = useOrderTypeContext();
    const { setGenerateToken, setExpiratedCode, setUser } = useLoginContext();

    const [ subtitle, setSubtitle ] = useState('');
    const [ isWhatsappAvailable, setIsWhatsappAvailable ] = useState<boolean | null>(null);
    const [ loadingButton, setLoadingButton ] = useState(false);
    const [userToken, setUserToken] = useState('');
    const { checkIsWhatsappAvailable } = useCartHook();

    const [ codeSent, setCodeSent ] = useState(false);
    const [userHash, setUserHash] = useState('');
    const [ enableResendCode, setEnableResendCode ] = useState(false);
    const [ timeResendCode, setTimeResendCode ] = useState('');
    const [invalidToken, setInvalidToken] = useState(false);
    const [ loadingResendCode, setLoadingResendCode ] = useState(false);
    const [receiveCodeType, setReceiveCodeType] = useState<string>('');

    const handleValidateToken = async () => {
        const payload = {
          user_token: userToken,
          user_hash: userHash,
          phone_number: phone
        }
        try {
          setLoadingButton(true);
          const customerService = new CustomerService(orderType);
          const validToken = await customerService.validateToken(payload);

          if (validToken.validated) {
            handleAuthenticated();
          } else {
            setInvalidToken(true);
          }

        } catch (error) {
            console.error('Error validateToken:', error);
            setInvalidToken(true);
        } finally {
            setLoadingButton(false);
        }
    }

    const handleAuthenticated = () => {
        setGenerateToken(null);
        setCodeSent(false);
        setUser((user: any) => ({...user, mobile_phone_confirmed: true}))
        onSuccess();
    }

    const handleSendCode = async (receiveCodeType: string) => {
        const payload = {
          media: receiveCodeType,
          number: phone
        };

        setLoadingButton(true);
        setReceiveCodeType(receiveCodeType);

        try {
            const customerService = new CustomerService(orderType);
            const generatedToken = await customerService.generateToken(payload);

            if (generatedToken) {
                setCodeSent(!generatedToken.error);
                setUserHash(generatedToken.hash);
                setGenerateToken(generatedToken);
                setEnableResendCode(false);
                setTimeResendCode(generatedToken.generated);
                setExpiratedCode(false);
            } else {
                setInvalidToken(true);
            }

        } catch (error) {
            console.error('Error generateToken:', error);
        } finally {
            setLoadingResendCode(false);
            setLoadingButton(false);
        }
    }

    const handleResendCode = () => {
        setUserToken('');
        setLoadingResendCode(true);
        handleSendCode(receiveCodeType);
        setInvalidToken(false);
    }

    const handleComplete = () => {
        setEnableResendCode(true);
    };

    useEffect(() => {
        const fetchWhatsappAvailability = async () => {
            const available = await checkIsWhatsappAvailable();
            setIsWhatsappAvailable(available);
        };

        fetchWhatsappAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (userToken.length === 6) {
          handleValidateToken();
          setInvalidToken(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userToken])

    useEffect(() => {
        if (timeResendCode) {
            setSubtitle(`${receiveCodeType === 'sms' ? t('profile.confirm_mobile_phone.receive_code_subtitle', {receiveCode: 'SMS'}) : t('profile.confirm_mobile_phone.receive_code_subtitle', {receiveCode: 'WhatsApp'})}`);
        } else {
            setSubtitle(t('profile.confirm_mobile_phone.subtitle'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeResendCode]);


    return (
            <div className='p-4 grid gap-4'>

                <h2 className='flex flex-col gap-3 items-center text-shopfood-title px-4'>
                    <TbDeviceMobileMessage size={50} className='zoomInIconNoDelay' />
                    <span className='text-xl font-extrabold text-shopfood-title leading-5'>{t('profile.confirm_mobile_phone.title')}</span>
                </h2>

                <div className='grid gap-2'>
                    <div className='leading-5 px-2' dangerouslySetInnerHTML={{ __html: sanitizeText(subtitle) }}></div>
                    <div className='text-2xl font-extrabold text-shopfood-title'>{ formatPhone(phone) }</div>
                </div>

                {timeResendCode && <div className='leading-5 text-gray-500 px-6'>{t('profile.confirm_mobile_phone.subtitle_to_finish')}</div>}

                {userHash &&
                    <div className='grid gap-1'>
                        <div className='relative flex items-center justify-between'>
                            <IMaskInput
                                mask="000000"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                id="code"
                                radix="."
                                value={userToken}
                                onAccept={(value) => setUserToken(value)}
                                unmask={true} // true|false|'typed'
                                placeholder='------'
                                className='relative tracking-[.25em] text-xl block w-full rounded border-0 py-2 pl-3 pr-12 ring-1 ring-gray-400 placeholder:text-gray-300 placeholder:text-xl placeholder:font-normal font-extrabold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200 disabled:bg-gray-200'
                            />

                            {codeSent &&
                                <ButtonPrimary outline={!enableResendCode} onClick={handleResendCode} disabled={!enableResendCode || loadingResendCode} className='absolute items-center justify-center right-0 mr-2 h-8 w-20 text-xs text-nowrap text-xs'>
                                { !enableResendCode
                                    ? <CountdownTimer  startDate={timeResendCode} durationInMinutes={3} onComplete={handleComplete} />
                                    : enableResendCode && !loadingResendCode ? t('profile.confirm_mobile_phone.resend_code_label') : <span className='flex items-center justify-center'><ImSpinner8 className="animate-spin" /></span>
                                }
                                </ButtonPrimary>
                            }
                        </div>
                        {invalidToken &&
                            <div className='text-xs text-left text-shopfood-error'>{t('profile.confirm_mobile_phone.invalid_token_message')}</div>
                        }
                    </div>
                }

                {!timeResendCode &&
                    <div className='grid gap-4 w-full mt-2'>
                        {(!phone || !receiveCodeType || (isPhoneValid(phone) && receiveCodeType === 'sms')) &&
                            <ButtonPrimary disabled={!isPhoneValid(phone) || loadingButton} onClick={() => handleSendCode('sms')} className='w-full gap-1.5'>
                                {loadingButton && receiveCodeType === 'sms' ? <><span><ImSpinner8 className="animate-spin" /></span><span>{t('profile.confirm_mobile_phone.sending_code_sms_loading')}</span></> : <><span>{t('profile.confirm_mobile_phone.sending_code_sms')}</span><TbMessage size={20} /></> }
                            </ButtonPrimary>
                        }

                        { (isWhatsappAvailable && (!phone || !receiveCodeType || (isPhoneValid(phone) && receiveCodeType === 'whatsapp'))) &&
                            <ButtonPrimary outline disabled={!isPhoneValid(phone) || loadingButton} onClick={() => handleSendCode('whatsapp')} className='w-full flex-col'>
                                <div className='flex items-center gap-1.5'>
                                {loadingButton && receiveCodeType === 'whatsapp' ? <><span><ImSpinner8 className="animate-spin" /></span><span>{t('profile.confirm_mobile_phone.sending_code_whatsapp_loading')}</span></> : <><span>{t('profile.confirm_mobile_phone.sending_code_whatsapp')}</span><TbBrandWhatsapp size={20} /></> }
                                </div>
                            </ButtonPrimary>
                        }
                    </div>
                }
            </div>
        )
    }

export default FormConfirmMobilePhone;